// src/components/pages/MyOrders.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getColors } from '../../styles/colors';
import { 
  FaHistory, FaCalendarAlt, FaClock, FaArrowLeft,
  FaLeaf, FaFish, FaTrash, FaShoppingCart,
  FaEye, FaSpinner, FaCheck, FaTimes,
  FaEdit, FaPlus, FaCalendarDay,
  FaInfoCircle, FaTimesCircle, FaList, FaArrowRight,
  FaMapMarkerAlt, FaLock, FaChevronLeft, FaChevronRight,
  FaBox, FaTag
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionListAsync } from '../../redux/slices/subscriptionSlice';
import { cancelOrderAsync } from '../../redux/slices/cartSlice';
import apiService from '../../api/authApi';

const MyOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { user } = useSelector((state) => state.auth || { user: null });
  
  // State
  const [subscriptions, setSubscriptions] = useState([]);
  const [cartOrders, setCartOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [productMap, setProductMap] = useState({});
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  
  // ✅ Load cancelled orders from localStorage on mount
  const loadCancelledOrders = useCallback(() => {
    try {
      const saved = localStorage.getItem('cancelledCustomOrders');
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log('📦 Loaded cancelled orders from localStorage:', parsed);
        return new Set(parsed);
      }
    } catch (e) {
      console.error('Failed to load cancelled orders:', e);
    }
    return new Set();
  }, []);

  // ✅ Save cancelled orders to localStorage
  const saveCancelledOrders = useCallback((cancelledSet) => {
    try {
      const data = [...cancelledSet];
      localStorage.setItem('cancelledCustomOrders', JSON.stringify(data));
      console.log('💾 Saved cancelled orders to localStorage:', data);
    } catch (e) {
      console.error('Failed to save cancelled orders:', e);
    }
  }, []);

  const [cancelledOrders, setCancelledOrders] = useState(loadCancelledOrders);

  useEffect(() => {
    saveCancelledOrders(cancelledOrders);
  }, [cancelledOrders, saveCancelledOrders]);

  useEffect(() => {
    const saved = loadCancelledOrders();
    setCancelledOrders(saved);
  }, [loadCancelledOrders]);
  
  // Meal Off Modal states
  const [showMealOffModal, setShowMealOffModal] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [draftMeals, setDraftMeals] = useState([]);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [mealOffLoading, setMealOffLoading] = useState(false);
  const [mealOffMessage, setMealOffMessage] = useState(null);
  const [isFetchingMealOffs, setIsFetchingMealOffs] = useState(false);

  const mealTimes = ['Breakfast', 'Lunch', 'Dinner'];
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const fetchInProgress = useRef(false);
  const mealOffFetchInProgress = useRef(false);
  const lastFetchedSubscriptionId = useRef(null);

  // Helper functions
  const getToken = useCallback(() => {
    return localStorage.getItem('token');
  }, []);

  const parseApiDate = (value) => {
    if (!value) return new Date();
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const toDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatReadableDate = (dateKey) => {
    if (!dateKey) return '';
    try {
      const [year, month, day] = dateKey.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateKey;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return '#6B7280';
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return '#F59E0B';
    switch (statusLower) {
      case 'active':
      case 'completed':
      case 'paid':
      case 'confirmed':
      case 'delivered':
        return '#22C55E';
      case 'pending':
      case 'pending_payment':
      case 'placed':
        return '#F59E0B';
      case 'processing':
        return '#3B82F6';
      default:
        return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    if (!status) return 'Unknown';
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return 'Cancelled';
    switch (statusLower) {
      case 'pending_payment': return 'Pending Payment';
      case 'pending': return 'Pending';
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'confirmed': return 'Confirmed';
      case 'placed': return 'Placed';
      case 'processing': return 'Processing';
      case 'delivered': return 'Delivered';
      default: return status;
    }
  };

  const getMealIcon = (mealTime) => {
    const icons = {
      'Breakfast': '🌅',
      'Lunch': '☀️',
      'Dinner': '🌙'
    };
    return icons[mealTime] || '🍽️';
  };

  const DEFAULT_IMAGE = 'https://ui-avatars.com/api/?name=Product&background=FF6B35&color=fff&size=128';

  const getCartItemDetails = useCallback((productId) => {
    if (!productId) return { name: 'Product', image: DEFAULT_IMAGE, type: 'Veg', price: 0 };
    const product = productMap[parseInt(productId)];
    return {
      name: product?.product_name || product?.name || `Product #${productId}`,
      image: product?.image_url || `https://ui-avatars.com/api/?name=Product&background=FF6B35&color=fff&size=128`,
      type: product?.type || 'Veg',
      price: product?.price || 0
    };
  }, [productMap]);

  const getProductImage = (productId) => {
    if (!productId) return DEFAULT_IMAGE;
    const product = productMap[parseInt(productId)];
    if (product && product.image_url) {
      return product.image_url;
    }
    const productName = getProductName(productId);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(productName)}&background=FF6B35&color=fff&size=128`;
  };

  const getProductName = (productId) => {
    if (!productId) return `Product #${productId}`;
    const product = productMap[parseInt(productId)];
    return product?.product_name || product?.name || `Product #${productId}`;
  };

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await apiService.getProducts();
      if (response?.status && response?.data) {
        const map = {};
        response.data.forEach(product => {
          let imageUrl = null;
          if (product.prod_image) {
            imageUrl = `https://admin.thefoodies.in/public/uploads/products/${product.prod_image}`;
          }
          map[product.id] = {
            ...product,
            image_url: imageUrl,
            name: product.product_name || `Product #${product.id}`,
            type: product.type || 'Veg',
            price: parseFloat(product.price) || 0
          };
        });
        setProductMap(map);
      }
    } catch (error) {
      console.error('❌ Failed to fetch products:', error);
    }
  }, [getToken]);

  const fetchSingleSubscriptionDetails = useCallback(async (subscriptionId) => {
    try {
      const token = getToken();
      if (!token) return null;
      
      const response = await apiService.getSubscriptionDetails(subscriptionId);
      
      if (response?.status && response?.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(`❌ Failed to fetch subscription ${subscriptionId}:`, error);
      return null;
    }
  }, [getToken]);

  const fetchMealOffsForSingleSubscription = useCallback(async (subscriptionId) => {
    if (mealOffFetchInProgress.current && lastFetchedSubscriptionId.current === subscriptionId) {
      return null;
    }

    try {
      mealOffFetchInProgress.current = true;
      lastFetchedSubscriptionId.current = subscriptionId;
      setIsFetchingMealOffs(true);
      
      const response = await apiService.getMealOffs(subscriptionId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to fetch meal offs for ${subscriptionId}:`, error);
      return null;
    } finally {
      mealOffFetchInProgress.current = false;
      setIsFetchingMealOffs(false);
      setTimeout(() => {
        lastFetchedSubscriptionId.current = null;
      }, 500);
    }
  }, []);

  // ========== CUSTOM ORDER API FUNCTIONS ==========

  const fetchCustomOrderDetails = useCallback(async (orderId) => {
    try {
      const token = getToken();
      if (!token) return null;
      
      console.log(`🔄 Fetching custom order details for ${orderId}...`);
      const response = await apiService.getCustomDateOrderDetails(orderId);
      console.log(`✅ Custom order ${orderId} details fetched:`, response);
      
      if (response?.status && response?.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(`❌ Failed to fetch custom order ${orderId}:`, error);
      return null;
    }
  }, [getToken]);

  // ✅ Fetch custom orders history using direct API call
  const fetchCustomOrdersHistory = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        console.warn('⚠️ No token found');
        return null;
      }
      
      console.log('🔄 Fetching custom orders history from API...');
      
      const response = await fetch('https://admin.thefoodies.in/api/tiffin/date-wise/history', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      console.log('✅ Custom orders history response:', data);

      if (response.ok && data.status && data.data) {
        const orders = data.data.map(order => {
          const orderId = order.order_id || order.id;
          const orderNumber = order.order_number || `TIF${orderId}`;
          const totalAmount = parseFloat(order.total_amount || order.total || 0);
          const status = order.order_status || order.payment_status || 'pending';
          const createdAt = order.created_at || order.date || new Date().toISOString();
          
          const items = order.items || [];
          const processedItems = items.map(item => ({
            ...item,
            product_name: item.product_name || `${item.meal_type} ${item.meal_time}`,
            name: item.name || `${item.meal_type} ${item.meal_time}`,
            meal_time: item.meal_time || 'Meal',
            meal_type: item.meal_type || 'Veg',
            price: parseFloat(item.price) || 0,
            quantity: parseInt(item.quantity) || 1,
            date: item.booking_date || item.date || order.date || createdAt,
            booking_date: item.booking_date
          }));

          return {
            order_id: orderId,
            order_number: orderNumber,
            total: totalAmount,
            total_amount: totalAmount,
            status: status,
            order_status: status,
            created_at: createdAt,
            date: createdAt,
            items: processedItems,
            delivery_address: order.customer_address || order.address || order.delivery_address || 'N/A',
            customer_name: order.customer_name || order.name,
            customer_phone: order.customer_phone || order.phone
          };
        });

        console.log(`✅ Loaded ${orders.length} custom orders`);
        return orders;
      } else {
        console.warn('⚠️ No custom orders found or API returned error:', data);
        return [];
      }
    } catch (error) {
      console.error('❌ Failed to fetch custom orders:', error);
      return null;
    }
  }, [getToken]);

  // Fetch all orders
  const fetchAllOrders = useCallback(async () => {
    if (fetchInProgress.current) {
      console.log('⏳ Fetch already in progress, skipping...');
      return;
    }

    fetchInProgress.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      if (!token) {
        setIsLoading(false);
        fetchInProgress.current = false;
        return;
      }

      await fetchProducts();

      // 1. Fetch subscription list
      let subscriptionsData = [];
      try {
        const subResult = await dispatch(getSubscriptionListAsync()).unwrap();
        if (subResult?.data && Array.isArray(subResult.data)) {
          subscriptionsData = subResult.data;
        }
      } catch (subError) {
        console.error('❌ Failed to fetch subscriptions:', subError);
      }

      const basicSubscriptions = subscriptionsData.map(sub => ({
        ...sub,
        current_total_after_offs: parseFloat(sub.current_total_after_offs) || parseFloat(sub.projected_total) || 0,
        outstanding_amount: parseFloat(sub.outstanding_amount) || 0,
        breakdown: [],
        total_meals: 0,
        delivery_address: sub.delivery_address || 'N/A',
        mealOffs: []
      }));
      
      setSubscriptions(basicSubscriptions);

      // 2. Fetch cart orders
      try {
        const response = await apiService.getOrderHistory();
        if (response?.status && response?.data) {
          const orders = response.data.map(order => ({
            order_id: order.order_id || order.id,
            total: parseFloat(order.total || order.total_amount || 0),
            status: order.status || 'pending',
            payment: order.payment || 'pending',
            created_at: order.created_at || new Date().toISOString(),
            items: order.items || [],
            customer_address: order.customer_address || order.address || order.delivery_address,
            customer_phone: order.customer_phone || order.phone,
            customer_name: order.customer_name || order.name,
          }));
          setCartOrders(orders);
        } else {
          setCartOrders([]);
        }
      } catch (cartError) {
        console.error('❌ Failed to fetch cart orders:', cartError);
        setCartOrders([]);
      }

      // 3. Fetch custom orders from API
      try {
        const customOrdersData = await fetchCustomOrdersHistory();
        if (customOrdersData && customOrdersData.length > 0) {
          const cancelledSet = loadCancelledOrders();
          const ordersWithStatus = customOrdersData.map(order => {
            const orderId = order.order_id || order.id;
            if (cancelledSet.has(orderId)) {
              return { ...order, status: 'cancelled' };
            }
            return order;
          });
          setCustomOrders(ordersWithStatus);
          console.log('✅ Loaded custom orders:', ordersWithStatus.length);
        } else {
          const savedOrders = localStorage.getItem('customOrders');
          if (savedOrders) {
            const parsedOrders = JSON.parse(savedOrders);
            setCustomOrders(parsedOrders);
          } else {
            setCustomOrders([]);
          }
        }
      } catch (localError) {
        console.error('❌ Failed to fetch custom orders:', localError);
        const savedOrders = localStorage.getItem('customOrders');
        if (savedOrders) {
          const parsedOrders = JSON.parse(savedOrders);
          setCustomOrders(parsedOrders);
        }
      }
      
    } catch (error) {
      console.error('❌ Failed to fetch orders:', error);
      setError('Failed to load orders. Please try again.');
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  }, [dispatch, fetchProducts, getToken, fetchCustomOrdersHistory, loadCancelledOrders]);

  useEffect(() => {
    fetchAllOrders();
  }, [fetchAllOrders, refreshKey]);

  // ========== CANCEL FUNCTIONS ==========
  
  const handleCancelCartOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    
    setCancellingOrderId(orderId);
    
    try {
      setCartOrders(prev => prev.map(order => 
        (order.order_id || order.id) === orderId 
          ? { ...order, status: 'cancelled' } 
          : order
      ));
      
      setCancelledOrders(prev => new Set(prev).add(orderId));
      
      if (selectedOrder && (selectedOrder.order_id === orderId || selectedOrder.id === orderId)) {
        setSelectedOrder(prev => ({
          ...prev,
          status: 'cancelled'
        }));
      }
      
      const result = await dispatch(cancelOrderAsync(orderId)).unwrap();
      
      if (result?.status) {
        alert('✅ Order cancelled successfully!');
        await fetchAllOrders();
      } else {
        setCartOrders(prev => prev.map(order => 
          (order.order_id || order.id) === orderId 
            ? { ...order, status: order.status } 
            : order
        ));
        setCancelledOrders(prev => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
        alert('❌ Failed to cancel order: ' + (result?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Failed to cancel order:', error);
      setCartOrders(prev => prev.map(order => 
        (order.order_id || order.id) === orderId 
          ? { ...order, status: order.status } 
          : order
      ));
      setCancelledOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
      alert('❌ Failed to cancel order. Please try again.');
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handleCancelCustomOrder = async (orderId) => {
    const token = getToken();
    if (!token) {
      alert('❌ Please login again. Token not found.');
      return;
    }

    const order = customOrders.find(o => (o.order_id || o.id) === orderId);
    if (order?.status?.toLowerCase() === 'cancelled' || cancelledOrders.has(orderId)) {
      alert('ℹ️ This order has already been cancelled.');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this custom order?')) return;
    
    setCancellingOrderId(orderId);
    
    try {
      setCustomOrders(prev => prev.map(order => 
        (order.order_id || order.id) === orderId 
          ? { ...order, status: 'cancelled' } 
          : order
      ));
      
      setCancelledOrders(prev => new Set(prev).add(orderId));
      
      if (selectedOrder && (selectedOrder.order_id === orderId || selectedOrder.id === orderId)) {
        setSelectedOrder(prev => ({
          ...prev,
          status: 'cancelled'
        }));
      }
      
      const savedOrders = JSON.parse(localStorage.getItem('customOrders') || '[]');
      const updatedSavedOrders = savedOrders.map(order => 
        (order.order_id || order.id) === orderId 
          ? { ...order, status: 'cancelled' } 
          : order
      );
      localStorage.setItem('customOrders', JSON.stringify(updatedSavedOrders));
      
      try {
        const response = await apiService.cancelCustomDateOrder(orderId);
        
        if (response?.status) {
          alert('✅ Custom order cancelled successfully!');
          await fetchAllOrders();
        } else {
          console.warn('⚠️ API returned error but keeping UI cancelled:', response);
          alert('⚠️ Order cancellation requested. Status updated locally.');
        }
      } catch (apiError) {
        console.warn('⚠️ API call failed but keeping UI cancelled:', apiError);
        alert('⚠️ Order cancellation requested. Status updated locally.');
      }
      
    } catch (error) {
      console.error('❌ Failed to cancel custom order:', error);
      alert('❌ Failed to cancel custom order. Please try again.');
      
      setCustomOrders(prev => prev.map(order => 
        (order.order_id || order.id) === orderId 
          ? { ...order, status: order.status } 
          : order
      ));
      setCancelledOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
      
      if (selectedOrder && (selectedOrder.order_id === orderId || selectedOrder.id === orderId)) {
        setSelectedOrder(prev => ({
          ...prev,
          status: order?.status || 'pending'
        }));
      }
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    const token = getToken();
    if (!token) {
      alert('❌ Please login again. Token not found.');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    try {
      await apiService.cancelSubscription(subscriptionId);
      alert('✅ Subscription cancelled successfully!');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('❌ Failed to cancel subscription:', error);
      alert('❌ Failed to cancel subscription. Please try again.');
    }
  };

  // ========== VIEW CUSTOM ORDER DETAILS ==========
  const handleViewCustomOrder = useCallback(async (orderId) => {
    try {
      setIsLoading(true);
      
      const existingOrder = customOrders.find(o => (o.order_id || o.id) === orderId);
      const isCancelled = cancelledOrders.has(orderId) || existingOrder?.status?.toLowerCase() === 'cancelled';
      
      if (isCancelled) {
        setSelectedOrder({
          ...existingOrder,
          orderType: 'custom',
          status: 'cancelled'
        });
        setShowOrderDetail(true);
        setIsLoading(false);
        return;
      }
      
      const details = await fetchCustomOrderDetails(orderId);
      if (details) {
        const items = details.items || existingOrder?.items || [];
        const processedItems = items.map(item => ({
          ...item,
          date: item.booking_date || item.date || details.date,
          booking_date: item.booking_date
        }));
        
        const orderWithDetails = {
          ...existingOrder,
          ...details,
          items: processedItems
        };
        setSelectedOrder({
          ...orderWithDetails,
          orderType: 'custom'
        });
        setShowOrderDetail(true);
      } else if (existingOrder) {
        setSelectedOrder({
          ...existingOrder,
          orderType: 'custom'
        });
        setShowOrderDetail(true);
      } else {
        alert('❌ Failed to load order details.');
      }
    } catch (error) {
      console.error('❌ Failed to load custom order:', error);
      alert('❌ Failed to load order details.');
    } finally {
      setIsLoading(false);
    }
  }, [customOrders, fetchCustomOrderDetails, cancelledOrders]);

  // ========== MEAL OFF FUNCTIONS ==========

  const openDateEditor = useCallback((date) => {
    if (!currentSubscription) {
      console.error('❌ No current subscription');
      return;
    }

    let dateKey;
    if (typeof date === 'string') {
      dateKey = date;
    } else if (date instanceof Date) {
      dateKey = toDateKey(date);
    } else {
      console.error('❌ Invalid date type:', typeof date);
      return;
    }
    
    const breakdown = currentSubscription.breakdown || [];

    const mealsForDate = mealTimes
      .map((mealTime) => {
        const items = breakdown.filter(
          (item) => item.date === dateKey && item.meal_time === mealTime
        );

        if (items.length === 0) return null;

        const initiallyEnabled = items.some((item) => !item.off);

        return {
          meal_time: mealTime,
          items: items,
          enabled: initiallyEnabled,
          initiallyEnabled: initiallyEnabled,
          totalQuantity: items.reduce((sum, item) => sum + (item.quantity || 1), 0),
          totalPrice: items.reduce((sum, item) => sum + (item.subtotal || 0), 0),
        };
      })
      .filter(meal => meal !== null);

    if (mealsForDate.length === 0) {
      setMealOffMessage({
        type: 'error',
        text: '⚠️ No meals found for this date.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    setSelectedDate(dateKey);
    setDraftMeals(mealsForDate);
    setSheetVisible(true);
  }, [currentSubscription, mealTimes]);

  const toggleMeal = useCallback((mealTime) => {
    setDraftMeals(prev =>
      prev.map(meal => {
        if (meal.meal_time === mealTime) {
          return { 
            ...meal, 
            enabled: !meal.enabled,
            meal_time: meal.meal_time || mealTime
          };
        }
        return meal;
      })
    );
  }, []);

  const updateSubscriptionForDate = useCallback(async () => {
    if (!selectedDate) {
      setMealOffMessage({
        type: 'error',
        text: '❌ No date selected. Please select a date first.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    if (!currentSubscription || !currentSubscription.id) {
      setMealOffMessage({
        type: 'error',
        text: '❌ No subscription selected.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    const mealsToTurnOff = draftMeals.filter(
      (meal) => meal.initiallyEnabled && !meal.enabled
    );

    if (mealsToTurnOff.length === 0) {
      setSheetVisible(false);
      return;
    }

    setMealOffLoading(true);

    try {
      const formattedDate = selectedDate;
      
      const promises = mealsToTurnOff.map((meal) => {
        const mealTime = meal.meal_time;
        let mealType = 'Veg';
        if (meal.items && meal.items.length > 0) {
          mealType = meal.items[0]?.meal_type || 'Veg';
        }
        
        return apiService.markMealOff(
          currentSubscription.id,
          formattedDate,
          mealTime,
          mealType
        );
      });

      await Promise.all(promises);

      setSheetVisible(false);
      
      setMealOffMessage({
        type: 'success',
        text: `✅ Updated ${mealsToTurnOff.length} meal(s) successfully!`
      });

      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
        setShowMealOffModal(false);
        setCurrentSubscription(null);
      }, 1000);

      setTimeout(() => setMealOffMessage(null), 3000);

    } catch (error) {
      console.error('❌ Failed to update meals:', error);
      
      let errorMessage = 'Failed to update meals. Please try again.';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMealOffMessage({
        type: 'error',
        text: `❌ ${errorMessage}`
      });
      setTimeout(() => setMealOffMessage(null), 5000);
    } finally {
      setMealOffLoading(false);
    }
  }, [selectedDate, currentSubscription, draftMeals]);

  const handleOpenMealOffModal = useCallback(async (subscription) => {
    const token = getToken();
    if (!token) {
      alert('❌ Please login again.');
      return;
    }

    if (subscription.status?.toLowerCase() !== 'active' && 
        subscription.status?.toLowerCase() !== 'pending_payment') {
      alert('❌ This subscription is not active.');
      return;
    }

    if (mealOffFetchInProgress.current) {
      return;
    }

    try {
      const details = await fetchSingleSubscriptionDetails(subscription.id);
      
      if (details) {
        const updatedSub = {
          ...subscription,
          ...details,
          breakdown: details.breakdown || [],
        };
        setCurrentSubscription(updatedSub);
        
        if (subscription.start_date) {
          setCalendarMonth(parseApiDate(subscription.start_date));
        }
        
        setShowMealOffModal(true);
        setSheetVisible(false);
        setSelectedDate(null);
        setDraftMeals([]);
        setMealOffMessage(null);
      } else {
        alert('❌ Failed to load subscription details.');
      }
    } catch (error) {
      console.error('❌ Failed to load subscription details:', error);
      alert('❌ Failed to load subscription details. Please try again.');
    }
  }, [fetchSingleSubscriptionDetails, getToken]);

  // ========== CALENDAR FUNCTIONS ==========
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDay };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(calendarMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarMonth(newDate);
  };

  const isDateWithinSubscription = (date) => {
    if (!currentSubscription?.start_date || !currentSubscription?.end_date) {
      return false;
    }
    const start = parseApiDate(currentSubscription.start_date);
    const end = parseApiDate(currentSubscription.end_date);
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return compareDate >= start && compareDate <= end;
  };

  const getPaymentColor = (payment) => {
    const paymentLower = payment?.toLowerCase() || '';
    if (paymentLower === 'paid' || paymentLower === 'completed') {
      return '#22C55E';
    } else if (paymentLower === 'pending' || paymentLower === 'pending_payment') {
      return '#F59E0B';
    } else if (paymentLower === 'failed') {
      return '#EF4444';
    }
    return '#6B7280';
  };

  // Get all orders for display
  const getAllOrders = () => {
    const all = [];
    
    cartOrders.forEach(order => {
      const total = parseFloat(order.total) || parseFloat(order.total_amount) || 0;
      all.push({
        ...order,
        orderType: 'cart',
        displayName: `Order #${order.order_id}`,
        date: order.created_at,
        status: order.status || 'pending',
        source: 'cart',
        items: order.items || [],
        total: total,
        order_number: order.order_id
      });
    });

    subscriptions.forEach(sub => {
      const total = parseFloat(sub.outstanding_amount) || parseFloat(sub.current_total_after_offs) || 0;
      const totalItems = sub.items?.length || 0;
      const mealCount = sub.total_meals || totalItems;
      
      all.push({
        ...sub,
        orderType: 'subscription',
        displayName: `Monthly Subscription #${sub.id}`,
        date: sub.start_date || sub.created_at,
        total: total,
        status: sub.status || 'pending',
        source: 'subscription',
        items: sub.items || [],
        breakdown: sub.breakdown || [],
        total_meals: mealCount,
        delivery_address: sub.delivery_address || 'N/A',
        mealOffs: sub.mealOffs || [],
        projected_total: sub.projected_total || 0,
        discount_percent: sub.discount_percent || 0,
        deposit_amount: sub.deposit_amount || 0,
        outstanding_amount: sub.outstanding_amount || 0,
        meals_by_type: sub.meals_by_type || {}
      });
    });

    customOrders.forEach(order => {
      const items = (order.items || []).map(item => {
        const productName = item.product_name || 
                           item.food_name || 
                           item.name || 
                           (item.meal_type && item.meal_time ? `${item.meal_type} ${item.meal_time}` : 'Meal');
        
        return {
          ...item,
          product_name: productName,
          name: productName,
          display_name: productName,
          meal_time: item.meal_time || 'Meal',
          meal_type: item.meal_type || 'Veg',
          price: parseFloat(item.price) || 0,
          quantity: parseInt(item.quantity) || 1,
          date: item.booking_date || item.date || order.date
        };
      });
      
      const orderId = order.order_id || order.id;
      const isCancelled = cancelledOrders.has(orderId) || order.status?.toLowerCase() === 'cancelled';
      
      all.push({
        ...order,
        orderType: 'custom',
        displayName: `Custom Order #${orderId}`,
        date: order.created_at || order.date || new Date().toISOString(),
        total: parseFloat(order.total_amount || order.total || 0),
        status: isCancelled ? 'cancelled' : (order.status || order.order_status || 'pending'),
        source: 'custom',
        items: items,
        order_id: orderId,
        order_number: order.order_number || `TIF${orderId}`
      });
    });

    all.sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      return dateB - dateA;
    });
    return all;
  };

  const getFilteredOrders = () => {
    const all = getAllOrders();
    if (activeTab === 'all') return all;
    return all.filter(o => o.orderType === activeTab);
  };

  const filteredOrders = getFilteredOrders();

  // ========== ORDER DETAIL VIEW ==========
  const OrderDetailView = () => {
    if (!selectedOrder) return null;

    const isSubscription = selectedOrder.orderType === 'subscription';
    const isCart = selectedOrder.orderType === 'cart';
    const isCustom = selectedOrder.orderType === 'custom';
    const isCancelled = selectedOrder.status?.toLowerCase() === 'cancelled' || 
                        cancelledOrders.has(selectedOrder.order_id || selectedOrder.id);
    const items = selectedOrder.items || [];
    const mealOffs = selectedOrder.mealOffs || [];
    const totalAmount = parseFloat(selectedOrder.total || 0);
    const originalTotal = parseFloat(selectedOrder.projected_total || 0);
    const outstandingAmount = parseFloat(selectedOrder.outstanding_amount || 0);
    const totalMeals = selectedOrder.total_meals || 0;
    const mealsByType = selectedOrder.meals_by_type || {};
    const depositAmount = parseFloat(selectedOrder.deposit_amount || 0);

    const canCancelCart = isCart && 
      (selectedOrder.status?.toLowerCase() === 'pending' || 
       selectedOrder.status?.toLowerCase() === 'placed' ||
       selectedOrder.status?.toLowerCase() === 'confirmed') &&
      !isCancelled;

    const canCancelCustom = isCustom && !isCancelled;

    const currentTotal = isSubscription && outstandingAmount > 0 
      ? outstandingAmount 
      : totalAmount;

    const displayOrderNumber = selectedOrder.order_number || `#${selectedOrder.order_id || selectedOrder.id || 'N/A'}`;

    // Group custom order items by date
    const groupItemsByDate = (items) => {
      if (!isCustom) return null;
      
      const grouped = {};
      items.forEach(item => {
        const dateKey = item.booking_date || item.date || selectedOrder.date;
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(item);
      });
      return grouped;
    };

    const groupedItems = isCustom ? groupItemsByDate(items) : null;
    const sortedDates = groupedItems ? Object.keys(groupedItems).sort() : [];

    return (
      <div className="space-y-4">
        <button
          onClick={() => { setShowOrderDetail(false); setSelectedOrder(null); }}
          className="flex items-center gap-2 text-sm font-medium transition-all hover:scale-105"
          style={{ color: colors.primary }}
        >
          <FaArrowLeft /> Back to Orders
        </button>

        <div className="rounded-2xl p-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-xl font-bold" style={{ color: colors.text }}>
              {isCart ? '🛒 Cart Order' : isSubscription ? '📆 Monthly Subscription' : '📅 Custom Order'}
            </h2>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: getStatusColor(isCancelled ? 'cancelled' : selectedOrder.status) }}>
                {isCancelled ? 'Cancelled' : getStatusText(selectedOrder.status)}
              </span>
              {isCart && selectedOrder.payment && !isCancelled && (
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ 
                  backgroundColor: getPaymentColor(selectedOrder.payment) + '20',
                  color: getPaymentColor(selectedOrder.payment)
                }}>
                  {selectedOrder.payment}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span style={{ color: colors.textSecondary }}>Order ID</span>
              <span style={{ color: colors.text }}>{displayOrderNumber}</span>
            </div>
            
            {isSubscription ? (
              <>
                <div className="flex justify-between">
                  <span style={{ color: colors.textSecondary }}>Start Date</span>
                  <span style={{ color: colors.text }}>{formatDate(selectedOrder.start_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: colors.textSecondary }}>End Date</span>
                  <span style={{ color: colors.text }}>{formatDate(selectedOrder.end_date)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between">
                <span style={{ color: colors.textSecondary }}>Date</span>
                <span style={{ color: colors.text }}>{formatDate(selectedOrder.created_at || selectedOrder.date)}</span>
              </div>
            )}
            
            {selectedOrder.delivery_address && selectedOrder.delivery_address !== 'N/A' && (
              <div className="flex justify-between col-span-2">
                <span style={{ color: colors.textSecondary }}>Delivery Address</span>
                <span style={{ color: colors.text }} className="text-right">{selectedOrder.delivery_address}</span>
              </div>
            )}
            
            {isSubscription && (
              <>
                {totalMeals > 0 && (
                  <div className="flex justify-between col-span-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                    <span style={{ color: colors.textSecondary }}>Total Meals</span>
                    <span style={{ color: colors.text }}>{totalMeals} meal{totalMeals !== 1 ? 's' : ''}</span>
                  </div>
                )}
                
                {Object.keys(mealsByType).length > 0 && (
                  <div className="flex justify-between col-span-2" style={{ borderColor: colors.border }}>
                    <span style={{ color: colors.textSecondary }}>Meal Details</span>
                    <div className="text-right">
                      {Object.entries(mealsByType).map(([key, count], idx) => {
                        const [mealTime, mealType] = key.split(' (');
                        const type = mealType ? mealType.replace(')', '') : 'Veg';
                        return (
                          <div key={idx} className="text-sm flex items-center gap-1 justify-end" style={{ color: colors.text }}>
                            <span>{getMealIcon(mealTime)}</span>
                            <span>{mealTime}</span>
                            <span className="text-[10px] flex items-center gap-0.5" style={{ 
                              color: type === 'Veg' ? '#22C55E' : '#EF4444'
                            }}>
                              {type === 'Veg' ? <FaLeaf className="text-[8px]" /> : <FaFish className="text-[8px]" />}
                              {type}: {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {depositAmount > 0 && (
                  <div className="flex justify-between col-span-2" style={{ borderColor: colors.border }}>
                    <span style={{ color: colors.textSecondary }}>Deposit (50%)</span>
                    <span style={{ color: colors.text }}>₹{depositAmount.toFixed(2)}</span>
                  </div>
                )}
                
                {outstandingAmount > 0 && selectedOrder.status?.toLowerCase() !== 'active' && (
                  <div className="flex justify-between col-span-2" style={{ borderColor: colors.border }}>
                    <span style={{ color: colors.textSecondary }}>Outstanding Amount</span>
                    <span style={{ color: '#EF4444' }}>₹{outstandingAmount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between col-span-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                  <span style={{ color: colors.textSecondary }}>Current Total</span>
                  <div className="text-right">
                    <span className="text-xl font-bold" style={{ color: colors.primary }}>
                      ₹{currentTotal.toFixed(2)}
                    </span>
                    {originalTotal > currentTotal && (
                      <p className="text-xs line-through" style={{ color: colors.textSecondary }}>
                        ₹{originalTotal.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            {!isSubscription && (
              <div className="flex justify-between col-span-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                <span style={{ color: colors.textSecondary }}>Total Amount</span>
                <div className="text-right">
                  <span className="text-xl font-bold" style={{ color: colors.primary }}>
                    ₹{totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Items Section - Grouped by Date for Custom Orders */}
          {items && items.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text }}>
                <FaShoppingCart className="text-sm" /> Items ({items.length})
              </h4>
              
              {isCustom && groupedItems ? (
                <div className="space-y-4">
                  {sortedDates.map(dateKey => {
                    const dateItems = groupedItems[dateKey];
                    const formattedDate = formatDate(dateKey);
                    
                    return (
                      <div key={dateKey} className="rounded-xl overflow-hidden" style={{ 
                        backgroundColor: colors.inputBackground,
                        border: `1px solid ${colors.border}`
                      }}>
                        <div className="px-4 py-2 flex items-center" style={{
                          backgroundColor: colors.primary + '15',
                          borderBottom: `1px solid ${colors.border}`
                        }}>
                          <span className="text-sm font-bold" style={{ color: colors.primary }}>
                            📅 {formattedDate}
                          </span>
                          <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full" style={{
                            backgroundColor: colors.primary + '20',
                            color: colors.primary
                          }}>
                            {dateItems.length} meal{dateItems.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        <div className="p-3 space-y-3">
                          {dateItems.map((item, idx) => {
                            const productName = item.product_name || item.name || item.food_name || 
                                               (item.meal_type && item.meal_time ? `${item.meal_type} ${item.meal_time}` : 'Meal');
                            const mealType = item.meal_type || 'Veg';
                            const mealTime = item.meal_time || 'Meal';
                            const quantity = parseInt(item.quantity) || 1;
                            const price = parseFloat(item.price) || 0;
                            const subtotal = price * quantity;
                            const isVeg = mealType === 'Veg' || mealType === 'veg';
                            
                            return (
                              <div 
                                key={idx} 
                                className="flex items-center gap-3 p-3 rounded-lg"
                                style={{ backgroundColor: colors.card }}
                              >
                                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                     style={{ backgroundColor: isVeg ? '#22C55E15' : '#EF444415' }}>
                                  <div className="w-4 h-4 rounded-full" 
                                       style={{ backgroundColor: isVeg ? '#22C55E' : '#EF4444' }} />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm" style={{ color: colors.text }}>
                                      {productName}
                                    </span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ 
                                      backgroundColor: isVeg ? '#22C55E20' : '#EF444420',
                                      color: isVeg ? '#22C55E' : '#EF4444'
                                    }}>
                                      {isVeg ? <FaLeaf className="text-[8px]" /> : <FaFish className="text-[8px]" />}
                                      {mealType}
                                    </span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                                      backgroundColor: colors.primary + '20',
                                      color: colors.primary
                                    }}>
                                      {mealTime}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
                                    <span>Qty: {quantity}</span>
                                    <span>₹{price.toFixed(2)}</span>
                                    <span className="font-medium" style={{ color: colors.primary }}>
                                      Total: ₹{subtotal.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : isCart ? (
                <div className="space-y-3">
                  {items.map((item, idx) => {
                    const productId = parseInt(item.product_id) || 0;
                    const productDetails = getCartItemDetails(productId);
                    const quantity = parseInt(item.quantity) || 1;
                    const price = parseFloat(item.price) || 0;
                    const subtotal = parseFloat(item.subtotal) || (price * quantity);
                    
                    return (
                      <div 
                        key={idx} 
                        className="flex items-center gap-3 p-3 rounded-lg"
                        style={{ backgroundColor: colors.inputBackground }}
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                          <img 
                            src={productDetails.image}
                            alt={productDetails.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(productDetails.name)}&background=FF6B35&color=fff&size=64`;
                            }}
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" style={{ color: colors.text }}>
                              {productDetails.name}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ 
                              backgroundColor: productDetails.type === 'Veg' ? '#22C55E20' : '#EF444420',
                              color: productDetails.type === 'Veg' ? '#22C55E' : '#EF4444'
                            }}>
                              {productDetails.type === 'Veg' ? <FaLeaf className="text-[10px]" /> : <FaFish className="text-[10px]" />}
                              {productDetails.type}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
                            <span>Qty: {quantity}</span>
                            <span>₹{price.toFixed(2)}</span>
                            <span className="font-medium" style={{ color: colors.primary }}>
                              Total: ₹{subtotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : isSubscription ? (
                <div className="space-y-3">
                  {items.map((item, idx) => {
                    const mealTime = item.meal_time || item.mealTime || 'Meal';
                    const mealType = item.meal_type || item.mealType || 'Veg';
                    const quantity = parseInt(item.quantity) || 1;
                    const price = parseFloat(item.price) || 0;
                    const subtotal = price * quantity;
                    const isVeg = mealType === 'Veg' || mealType === 'veg';
                    
                    return (
                      <div 
                        key={idx} 
                        className="flex items-center gap-3 p-3 rounded-lg"
                        style={{ backgroundColor: colors.inputBackground }}
                      >
                        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                             style={{ backgroundColor: isVeg ? '#22C55E15' : '#EF444415' }}>
                          <div className="w-5 h-5 rounded-full" 
                               style={{ backgroundColor: isVeg ? '#22C55E' : '#EF4444' }} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" style={{ color: colors.text }}>
                              {mealTime}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                              backgroundColor: isVeg ? '#22C55E20' : '#EF444420',
                              color: isVeg ? '#22C55E' : '#EF4444'
                            }}>
                              {mealType}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
                            <span>Qty: {quantity}</span>
                            {price > 0 && <span>₹{price.toFixed(2)}</span>}
                            <span className="font-medium" style={{ color: colors.primary }}>
                              Total: ₹{subtotal.toFixed(2)}
                            </span>
                          </div>
                          {item.food_name && (
                            <div className="text-[10px]" style={{ color: colors.textSecondary }}>
                              {item.food_name}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          )}

          {isSubscription && mealOffs.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: colors.text }}>
                <FaCalendarDay className="text-sm" style={{ color: '#F59E0B' }} />
                Skipped Meals ({mealOffs.length})
              </h4>
              <div className="space-y-2">
                {mealOffs.map((mealOff, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded-lg"
                    style={{ 
                      backgroundColor: '#FEF3C7',
                      border: '1px solid #FCD34D'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{getMealIcon(mealOff.meal_time)}</span>
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                          {formatDateFull(mealOff.date)}
                        </p>
                        <div className="flex items-center gap-2 text-xs" style={{ color: '#92400E' }}>
                          <span>{mealOff.meal_time}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            {mealOff.meal_type?.toLowerCase() === 'veg' ? (
                              <FaLeaf className="text-green-600 text-[10px]" />
                            ) : (
                              <FaFish className="text-red-600 text-[10px]" />
                            )}
                            {mealOff.meal_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-3" style={{ borderColor: colors.border }}>
            {isSubscription && !isCancelled && (
              <button
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: colors.primary + '15',
                  color: colors.primary,
                  border: `1px solid ${colors.primary}30`
                }}
                onClick={() => {
                  setShowOrderDetail(false);
                  handleOpenMealOffModal(selectedOrder);
                }}
              >
                <FaEdit className="text-sm" /> Modify Subscription
              </button>
            )}
            
            {isSubscription && !isCancelled && (
              <button
                onClick={() => handleCancelSubscription(selectedOrder.id)}
                className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
              >
                <FaTrash className="text-sm" /> Cancel Subscription
              </button>
            )}

            {isCart && canCancelCart && (
              <button
                onClick={() => handleCancelCartOrder(selectedOrder.order_id || selectedOrder.id)}
                className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
                disabled={cancellingOrderId === (selectedOrder.order_id || selectedOrder.id)}
              >
                {cancellingOrderId === (selectedOrder.order_id || selectedOrder.id) ? (
                  <><FaSpinner className="animate-spin" /> Cancelling...</>
                ) : (
                  <><FaTrash className="text-sm" /> Cancel Order</>
                )}
              </button>
            )}

            {isCustom && !isCancelled && (
              <button
                onClick={() => handleCancelCustomOrder(selectedOrder.order_id || selectedOrder.id)}
                className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
                disabled={cancellingOrderId === (selectedOrder.order_id || selectedOrder.id)}
              >
                {cancellingOrderId === (selectedOrder.order_id || selectedOrder.id) ? (
                  <><FaSpinner className="animate-spin" /> Cancelling...</>
                ) : (
                  <><FaTrash className="text-sm" /> Cancel Custom Order</>
                )}
              </button>
            )}

            {isCustom && isCancelled && (
              <div className="w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                   style={{ 
                     backgroundColor: '#FEF3C7',
                     color: '#92400E',
                     border: '1px solid #FCD34D'
                   }}>
                <FaTimes className="text-sm" /> This order has been cancelled
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========== MEAL OFF MODAL COMPONENT ==========
  const MealOffModalComponent = () => {
    if (!showMealOffModal || !currentSubscription) return null;

    const { daysInMonth, firstDay } = getDaysInMonth(calendarMonth);
    const monthName = monthNames[calendarMonth.getMonth()];
    const year = calendarMonth.getFullYear();

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-10 h-10" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
      const dateKey = toDateKey(date);
      const isActive = isDateWithinSubscription(date);
      const isToday = selectedDate === dateKey;

      const allMeals = (currentSubscription.breakdown || []).filter(
        item => item.date === dateKey
      );
      
      const totalMealsForDate = allMeals.length;
      const skippedMealsForDate = allMeals.filter(item => item.off === true).length;
      const bookedMealsCount = totalMealsForDate - skippedMealsForDate;
      
      let dotCount = 0;
      if (totalMealsForDate > 0) {
        dotCount = bookedMealsCount;
      }

      days.push(
        <button
          key={day}
          className={`w-10 h-10 rounded-lg text-sm font-medium transition-all hover:scale-105 relative ${
            !isActive ? 'opacity-30 cursor-not-allowed' : ''
          } ${isToday ? 'ring-2 ring-offset-2' : ''}`}
          style={{
            backgroundColor: isToday ? colors.primary : (isActive ? colors.inputBackground : 'transparent'),
            color: isToday ? '#FFFFFF' : (isActive ? colors.text : colors.textSecondary),
            borderColor: isToday ? colors.primary : colors.border,
            cursor: isActive ? 'pointer' : 'not-allowed'
          }}
          onClick={() => {
            if (isActive) {
              openDateEditor(date);
            }
          }}
          disabled={!isActive}
        >
          {day}
          {isActive && dotCount > 0 && (
            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
              {[...Array(dotCount)].map((_, idx) => (
                <span key={idx} className="w-1 h-1 rounded-full" style={{ backgroundColor: colors.primary }} />
              ))}
            </div>
          )}
          {isActive && totalMealsForDate > 0 && dotCount === 0 && (
            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
              <span className="text-[8px]" style={{ color: colors.textSecondary }}>✕</span>
            </div>
          )}
        </button>
      );
    }

    return (
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div 
            className="rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: colors.text }}>
                  <FaEdit style={{ color: colors.primary }} />
                  Modify Booking
                </h3>
                <p className="text-xs" style={{ color: colors.textSecondary }}>
                  Select a date to change its meals
                </p>
              </div>
              <button 
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                style={{ backgroundColor: colors.inputBackground }}
                onClick={() => {
                  setShowMealOffModal(false);
                  setSheetVisible(false);
                  setMealOffMessage(null);
                  setCurrentSubscription(null);
                  fetchAllOrders();
                }}
              >
                <FaTimes style={{ color: colors.text }} />
              </button>
            </div>

            <div className="p-3 rounded-xl mb-4" style={{ 
              backgroundColor: colors.inputBackground,
              border: `1px solid ${colors.border}`
            }}>
              <p className="text-sm font-semibold" style={{ color: colors.text }}>
                Subscription #{currentSubscription.id}
              </p>
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                {currentSubscription.start_date ? formatReadableDate(currentSubscription.start_date) : ''} – 
                {currentSubscription.end_date ? formatReadableDate(currentSubscription.end_date) : ''}
              </p>
              <p className="text-xs font-bold mt-1" style={{ color: colors.primary }}>
                Current Total: ₹{(currentSubscription.outstanding_amount || currentSubscription.current_total_after_offs || 0).toFixed(2)}
              </p>
            </div>

            {mealOffMessage && (
              <div className={`p-3 rounded-xl mb-4 text-sm flex items-center gap-2 ${
                mealOffMessage.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-600'
              }`}>
                {mealOffMessage.type === 'success' ? <FaCheck /> : <FaTimes />}
                {mealOffMessage.text}
              </div>
            )}

            <p className="text-xs mb-4" style={{ color: colors.textSecondary }}>
              Tap an active date to turn off a meal for that day.
            </p>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <button
                  className="p-1.5 rounded-lg transition-all hover:scale-105"
                  style={{ backgroundColor: colors.inputBackground }}
                  onClick={() => navigateMonth(-1)}
                >
                  <FaChevronLeft className="text-xs" style={{ color: colors.text }} />
                </button>
                <span className="text-sm font-medium" style={{ color: colors.text }}>
                  {monthName} {year}
                </span>
                <button
                  className="p-1.5 rounded-lg transition-all hover:scale-105"
                  style={{ backgroundColor: colors.inputBackground }}
                  onClick={() => navigateMonth(1)}
                >
                  <FaChevronRight className="text-xs" style={{ color: colors.text }} />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-1">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className="text-center text-[10px] font-medium" style={{ color: colors.textSecondary }}>
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {days}
              </div>
            </div>

            <div className="flex items-center gap-4 text-[10px] flex-wrap" style={{ color: colors.textSecondary }}>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.primary }} />
                Selected
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.inputBackground }} />
                Available
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: 'transparent', border: '1px solid #D1D5DB' }} />
                Inactive
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[8px]" style={{ color: colors.textSecondary }}>✕</span>
                <span>All skipped</span>
              </div>
            </div>
          </div>
        </div>

        {sheetVisible && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div 
              className="w-full max-w-md rounded-t-2xl p-6 max-h-[82%] overflow-y-auto"
              style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
            >
              <div className="flex justify-center mb-4">
                <div className="w-12 h-1 rounded-full" style={{ backgroundColor: colors.border }} />
              </div>

              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 pr-3">
                  <h4 className="text-lg font-bold" style={{ color: colors.text }}>
                    {selectedDate ? formatReadableDate(selectedDate) : ''}
                  </h4>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>
                    Uncheck a meal to opt out for this date.
                  </p>
                </div>
                <button
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: colors.inputBackground }}
                  onClick={() => setSheetVisible(false)}
                >
                  <FaTimes style={{ color: colors.text, fontSize: '14px' }} />
                </button>
              </div>

              <div className="space-y-3">
                {draftMeals.map((meal) => (
                  <div
                    key={meal.meal_time}
                    className="p-4 rounded-xl border"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      borderColor: colors.border,
                      opacity: meal.enabled ? 1 : 0.6
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <button
                        className="w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: meal.enabled ? colors.primary : colors.border,
                          backgroundColor: meal.enabled ? colors.primary : 'transparent'
                        }}
                        onClick={() => toggleMeal(meal.meal_time)}
                        disabled={!meal.initiallyEnabled}
                      >
                        {meal.enabled && <FaCheck style={{ color: '#FFFFFF', fontSize: '12px' }} />}
                      </button>
                      <span className="font-semibold text-sm" style={{ color: colors.text }}>
                        {meal.meal_time} {meal.enabled ? '· Booked' : '· Not booked'}
                      </span>
                    </div>

                    {meal.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 border-t text-sm"
                        style={{ borderColor: colors.border }}
                      >
                        <div className="flex items-center gap-2">
                          {item.meal_type === 'Veg' ? (
                            <FaLeaf style={{ color: '#22C55E' }} />
                          ) : (
                            <FaFish style={{ color: '#EF4444' }} />
                          )}
                          <span style={{ color: colors.textSecondary }}>
                            {item.meal_type || 'Veg'} · {item.food_name || 'Meal'} × {item.quantity || 1}
                          </span>
                        </div>
                        <span className="font-bold" style={{ color: colors.text }}>
                          ₹{(item.price || 0).toFixed(2)}
                        </span>
                      </div>
                    ))}

                    <div className="mt-2 pt-2 border-t flex justify-end" style={{ borderColor: colors.border }}>
                      <span className="font-bold" style={{ color: colors.text }}>
                        Total: ₹{meal.totalPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="w-full py-3 rounded-xl text-sm font-medium text-white transition-all hover:scale-[1.02] mt-4"
                style={{ 
                  backgroundColor: colors.primary,
                  opacity: mealOffLoading ? 0.7 : 1
                }}
                onClick={updateSubscriptionForDate}
                disabled={mealOffLoading}
              >
                {mealOffLoading ? (
                  <FaSpinner className="animate-spin mx-auto" />
                ) : (
                  'Update Order'
                )}
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="relative pt-6 pb-4 px-4 border-b"
           style={{
             background: `linear-gradient(180deg, ${colors.primary}20 0%, ${colors.background} 100%)`,
             borderColor: colors.border
           }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.text }}>
              <FaHistory style={{ color: colors.primary }} />
              My Orders
            </h1>
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              <span style={{ color: colors.textSecondary }}>📱 {user?.phone || '6295028775'}</span>
            </p>
          </div>
          <button
            onClick={() => navigate('/profile')}
            className="px-4 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.inputBackground,
              color: colors.text,
              border: `1px solid ${colors.border}`
            }}
          >
            Back to Profile
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 max-w-4xl">
        {showOrderDetail ? (
          <OrderDetailView />
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 overflow-x-auto">
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'all' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'all' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'all' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('all')}
              >
                All Orders
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'cart' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'cart' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'cart' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('cart')}
              >
                <FaShoppingCart className="inline mr-1.5" /> Cart Orders
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'subscription' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'subscription' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'subscription' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('subscription')}
              >
                <FaCalendarAlt className="inline mr-1.5" /> Monthly
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'custom' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'custom' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'custom' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('custom')}
              >
                <FaClock className="inline mr-1.5" /> Custom
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 rounded-lg text-sm flex items-start gap-2"
                   style={{ 
                     backgroundColor: '#FEF2F2',
                     color: '#DC2626',
                     border: '1px solid #FECACA'
                   }}>
                <span className="mt-0.5">⚠️</span>
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-xs font-medium hover:underline"
                  style={{ color: '#DC2626' }}
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Orders List */}
            {isLoading ? (
              <div className="text-center py-12">
                <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: colors.primary }} />
                <p className="mt-4" style={{ color: colors.textSecondary }}>Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>No Orders Yet</h3>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  {activeTab === 'all' 
                    ? "You haven't placed any orders yet." 
                    : activeTab === 'cart'
                    ? "You don't have any cart orders."
                    : activeTab === 'subscription' 
                    ? "You don't have any monthly subscriptions." 
                    : "You don't have any custom date orders."}
                </p>
                <button
                  onClick={() => navigate('/cart-service')}
                  className="mt-4 px-6 py-2 rounded-full text-white font-medium transition-all hover:scale-105"
                  style={{ backgroundColor: colors.primary }}
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map((order, index) => {
                  const orderItems = order.items || [];
                  const isCartOrder = order.orderType === 'cart';
                  const isSubscription = order.orderType === 'subscription';
                  const isCustomOrder = order.orderType === 'custom';
                  const mealOffs = order.mealOffs || [];
                  const totalAmount = parseFloat(order.total || 0);
                  const originalTotal = parseFloat(order.projected_total || 0);
                  const depositAmount = parseFloat(order.deposit_amount || 0);
                  const totalMeals = order.total_meals || 0;
                  const outstandingAmount = parseFloat(order.outstanding_amount || 0);
                  const discountPercent = order.discount_percent || 0;
                  
                  const isCancelled = order.status?.toLowerCase() === 'cancelled' || 
                                    cancelledOrders.has(order.order_id || order.id);
                  
                  const canCancelCart = isCartOrder && 
                    (order.status?.toLowerCase() === 'pending' || 
                     order.status?.toLowerCase() === 'placed' ||
                     order.status?.toLowerCase() === 'confirmed') &&
                    !isCancelled;
                  
                  const displayTotal = isSubscription && outstandingAmount > 0 
                    ? outstandingAmount 
                    : totalAmount;
                  
                  const hasDiscount = isSubscription && discountPercent > 0;
                  
                  // ✅ Get display name with order number
                  let displayName = '';
                  if (isCartOrder) {
                    displayName = `Order #${order.order_id}`;
                  } else if (isSubscription) {
                    displayName = `Monthly Subscription #${order.id}`;
                  } else if (isCustomOrder) {
                    displayName = `Custom Order #${order.order_id || order.id}`;
                  }
                  
                  return (
                    <div
                      key={index}
                      className="rounded-xl p-4 cursor-pointer transition-all hover:shadow-md"
                      style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowOrderDetail(true);
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-lg">
                              {isCartOrder ? '🛒' : isSubscription ? '📆' : '📅'}
                            </span>
                            <p className="font-medium" style={{ color: colors.text }}>
                              {isCartOrder 
                                ? `Order #${order.order_id}` 
                                : isSubscription 
                                  ? `Monthly Subscription #${order.id}` 
                                  : `Custom Order #${order.order_id || order.id}`}
                            </p>
                            {isCartOrder && order.payment && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                                backgroundColor: getPaymentColor(order.payment) + '20',
                                color: getPaymentColor(order.payment)
                              }}>
                                {order.payment}
                              </span>
                            )}
                            {isSubscription && hasDiscount && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">
                                {discountPercent}% Off
                              </span>
                            )}
                          </div>
                          
                          {isSubscription && (
                            <div className="mt-1">
                              <p className="text-xs" style={{ color: colors.textSecondary }}>
                                📅 {formatDate(order.start_date)} - {formatDate(order.end_date)}
                              </p>
                              <div className="flex items-center gap-3 text-xs flex-wrap" style={{ color: colors.textSecondary }}>
                                {totalMeals > 0 && (
                                  <span>🍽️ {totalMeals} meal{totalMeals !== 1 ? 's' : ''}</span>
                                )}
                                {depositAmount > 0 && (
                                  <span>💰 Deposit: ₹{depositAmount.toFixed(2)}</span>
                                )}
                                {outstandingAmount > 0 && order.status?.toLowerCase() !== 'active' && (
                                  <span style={{ color: '#EF4444' }}>Remaining: ₹{outstandingAmount.toFixed(2)}</span>
                                )}
                                {order.delivery_address && order.delivery_address !== 'N/A' && (
                                  <span className="flex items-center gap-1">
                                    <FaMapMarkerAlt className="text-[10px]" /> {order.delivery_address}
                                  </span>
                                )}
                              </div>
                              {order.meals_by_type && Object.keys(order.meals_by_type).length > 0 && (
                                <div className="flex items-center gap-2 text-xs flex-wrap mt-1">
                                  <span style={{ color: colors.textSecondary }}>Meals:</span>
                                  {Object.entries(order.meals_by_type).map(([key, count], idx) => {
                                    const [mealTime, mealType] = key.split(' (');
                                    const type = mealType ? mealType.replace(')', '') : 'Veg';
                                    return (
                                      <span key={idx} className="px-2 py-0.5 rounded-full flex items-center gap-1" style={{ 
                                        backgroundColor: type === 'Veg' ? '#22C55E20' : '#EF444420',
                                        color: type === 'Veg' ? '#22C55E' : '#EF4444'
                                      }}>
                                        {getMealIcon(mealTime)}
                                        {mealTime} ×{count}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {isSubscription ? (
                            totalMeals > 0 && (
                              <p className="text-xs" style={{ color: colors.textSecondary }}>
                                {totalMeals} meal{totalMeals > 1 ? 's' : ''}
                              </p>
                            )
                          ) : (
                            orderItems.length > 0 && (
                              <p className="text-xs" style={{ color: colors.textSecondary }}>
                                {orderItems.length} item{orderItems.length > 1 ? 's' : ''}
                              </p>
                            )
                          )}
                          
                          {isSubscription && mealOffs.length > 0 && (
                            <p className="text-xs flex items-center gap-1" style={{ color: '#F59E0B' }}>
                              <FaCalendarDay className="text-[10px]" />
                              {mealOffs.length} skipped meal{mealOffs.length > 1 ? 's' : ''}
                            </p>
                          )}
                          
                          {/* ✅ Show order number for custom orders */}
                          {isCustomOrder && order.order_number && (
                            <p className="text-xs" style={{ color: colors.textSecondary }}>
                              Order #: {order.order_number}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <p className="font-bold" style={{ color: colors.primary }}>
                            ₹{displayTotal.toFixed(2)}
                          </p>
                          {isSubscription && originalTotal > 0 && (
                            <p className="text-xs line-through" style={{ color: colors.textSecondary }}>
                              ₹{originalTotal.toFixed(2)}
                            </p>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: getStatusColor(isCancelled ? 'cancelled' : order.status) }}>
                            {isCancelled ? 'Cancelled' : getStatusText(order.status)}
                          </span>
                          
                          {/* Custom Order - Cancel Button (if not cancelled) */}
                          {isCustomOrder && !isCancelled && (
                            <button
                              className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center"
                              style={{ 
                                backgroundColor: '#EF444420',
                                color: '#EF4444',
                                border: '1px solid #EF444430'
                              }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handleCancelCustomOrder(order.order_id || order.id);
                              }}
                              disabled={cancellingOrderId === (order.order_id || order.id)}
                            >
                              {cancellingOrderId === (order.order_id || order.id) ? (
                                <FaSpinner className="animate-spin text-[10px]" />
                              ) : (
                                <>
                                  <FaTrash className="text-[10px]" /> Cancel
                                </>
                              )}
                            </button>
                          )}
                          
                          {/* Custom Order - Cancelled Badge */}
                          {isCustomOrder && isCancelled && (
                            <div className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center" style={{ 
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              border: '1px solid #FCD34D'
                            }}>
                              <FaTimes className="text-[10px]" /> Cancelled
                            </div>
                          )}
                          
                          {/* Cart Order - Cancel Button */}
                          {canCancelCart && (
                            <button
                              className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center"
                              style={{ 
                                backgroundColor: '#EF444420',
                                color: '#EF4444',
                                border: '1px solid #EF444430'
                              }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handleCancelCartOrder(order.order_id);
                              }}
                              disabled={cancellingOrderId === order.order_id}
                            >
                              {cancellingOrderId === order.order_id ? (
                                <FaSpinner className="animate-spin text-[10px]" />
                              ) : (
                                <>
                                  <FaTrash className="text-[10px]" /> Cancel
                                </>
                              )}
                            </button>
                          )}
                          
                          {/* Cart Order - Cancelled Badge */}
                          {isCartOrder && isCancelled && (
                            <div className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center" style={{ 
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              border: '1px solid #FCD34D'
                            }}>
                              <FaTimes className="text-[10px]" /> Cancelled
                            </div>
                          )}
                          
                          {/* Subscription - Modify Button */}
                          {isSubscription && order.status?.toLowerCase() !== 'cancelled' && (
                            <button
                              className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center"
                              style={{ 
                                backgroundColor: colors.primary + '20',
                                color: colors.primary
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMealOffModal(order);
                              }}
                              disabled={isFetchingMealOffs}
                            >
                              {isFetchingMealOffs ? (
                                <FaSpinner className="animate-spin text-[10px]" />
                              ) : (
                                <FaEdit className="text-[10px]" />
                              )}
                              Modify
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {orderItems.length > 0 && (
                        <div className="mt-2 pt-2 border-t flex gap-2 flex-wrap" style={{ borderColor: colors.border }}>
                          {orderItems.slice(0, 4).map((item, idx) => {
                            if (isSubscription) {
                              const mealTime = item.meal_time || item.mealTime || 'Meal';
                              const mealType = item.meal_type || item.mealType || 'Veg';
                              const quantity = parseInt(item.quantity) || 1;
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary }}
                                >
                                  <span>{getMealIcon(mealTime)}</span>
                                  <span>{mealTime}</span>
                                  <span className="text-[10px] flex items-center gap-0.5" style={{ 
                                    color: mealType === 'Veg' ? '#22C55E' : '#EF4444'
                                  }}>
                                    {mealType === 'Veg' ? <FaLeaf className="text-[8px]" /> : <FaFish className="text-[8px]" />}
                                    {mealType}
                                  </span>
                                  {quantity > 1 && <span>×{quantity}</span>}
                                </div>
                              );
                            } else if (isCustomOrder) {
                              const productName = item.product_name || item.name || item.food_name || 
                                                 (item.meal_type && item.meal_time ? `${item.meal_type} ${item.meal_time}` : 'Meal');
                              const quantity = parseInt(item.quantity) || 1;
                              const mealType = item.meal_type || 'Veg';
                              const mealTime = item.meal_time || 'Meal';
                              const isVeg = mealType === 'Veg' || mealType === 'veg';
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary }}
                                >
                                  <div className="w-3 h-3 rounded-full" 
                                       style={{ backgroundColor: isVeg ? '#22C55E' : '#EF4444' }} />
                                  <span className="truncate max-w-[80px]">{productName}</span>
                                  <span className="text-[10px]" style={{ 
                                    color: isVeg ? '#22C55E' : '#EF4444'
                                  }}>
                                    {mealType}
                                  </span>
                                  {quantity > 1 && <span>×{quantity}</span>}
                                </div>
                              );
                            } else {
                              const productId = parseInt(item.product_id);
                              const productImage = getProductImage(productId);
                              const productName = getProductName(productId);
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary }}
                                >
                                  <img 
                                    src={productImage}
                                    alt={productName}
                                    className="w-5 h-5 rounded-full object-cover"
                                    onError={(e) => {
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(productName)}&background=FF6B35&color=fff&size=32`;
                                    }}
                                  />
                                  <span>{productName}</span>
                                  {item.quantity && <span>×{item.quantity}</span>}
                                </div>
                              );
                            }
                          })}
                          {orderItems.length > 4 && (
                            <span className="text-xs px-2 py-1" style={{ color: colors.textSecondary }}>
                              +{orderItems.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-2 flex justify-end">
                        <button
                          className="text-xs flex items-center gap-1 px-3 py-1 rounded-lg transition-all hover:scale-105"
                          style={{ 
                            backgroundColor: colors.primary + '20',
                            color: colors.primary
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isCustomOrder) {
                              handleViewCustomOrder(order.order_id || order.id);
                            } else {
                              setSelectedOrder(order);
                              setShowOrderDetail(true);
                            }
                          }}
                        >
                          <FaEye className="text-xs" /> View Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Meal Off Modal Component */}
      <MealOffModalComponent />
    </div>
  );
};

export default MyOrders;